import { Link, useLocation } from 'react-router-dom'
import { routes } from '@/router'
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import {
  AttachMoney,
  CreditCard,
  Group,
  HomeOutlined,
  Logout,
  ReceiptOutlined,
  ShieldOutlined,
  RequestQuote,
  Share,
  SwapVert,
  Widgets,
} from '@mui/icons-material'
import { useMemo } from 'react'
import { useLogout } from '@/api/user-manager'
import { isNewFeature } from '@/helpers'
import { NewFeatureChip } from '../../new-feature-chip'
import { CurrentPlanCard } from '../../current-plan-card'
import AlumeLogo from '@/assets/images/logo-alume-invertido.svg'
import { UserProfile } from '@/types'
import { useCompany } from '@/context/companies'

const DRAWER_WIDTH = 300

export function DesktopNavigation() {
  const { pathname } = useLocation()

  const { deal } = useCompany()

  const { mutateAsync: logout } = useLogout()

  const isInsuranceNew = isNewFeature('insurance')

  const isAdmin = useMemo(() => {
    const user = JSON.parse(
      localStorage.getItem('user-profile') || '{}',
    ) as UserProfile

    return (
      deal.deal_users.find((dealUser) => dealUser.profile === 'admin')
        ?.user_key === user.key
    )
  }, [deal])

  const navigationRoutes = useMemo(
    () => [
      {
        name: 'Home',
        route: routes.dashboard,
        icon: <HomeOutlined />,
      },
      {
        name: 'Faturamento',
        route: routes.revenue,
        icon: <AttachMoney />,
      },
      {
        name: 'Notas Fiscais',
        route: routes.fiscalNotes.index,
        icon: <ReceiptOutlined />,
      },
      {
        name: 'Movimento mensal',
        route: routes.monthlyDocuments,
        icon: <SwapVert />,
      },
      {
        name: 'Guias de Impostos',
        route: routes.taxes,
        icon: <RequestQuote />,
      },
      {
        name: 'Sócios',
        route: routes.partners.index,
        icon: <Group />,
        hide: !isAdmin,
      },
      {
        name: 'Colaboradores',
        route: routes.collaborators,
        icon: <Group />,
        hide: !isAdmin,
      },
      {
        name: 'Serviços adicionais',
        route: routes.additionalServices,
        icon: <Widgets />,
      },
      {
        name: 'Seguros',
        route: routes.insurance,
        icon: <ShieldOutlined />,
        isNew: isInsuranceNew,
      },
      {
        name: 'Mensalidades',
        route: routes.billing,
        icon: <CreditCard />,
      },
      {
        name: 'Indique e Ganhe',
        route: routes.referAndEarn,
        icon: <Share />,
      },
    ],
    [isAdmin, isInsuranceNew],
  )

  return (
    <Drawer
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          gap: 3,
          color: 'white',
          bgcolor: (theme) => theme.palette.primary.main,
        },
      }}
      variant="permanent"
      anchor="left"
    >
      <img
        src={AlumeLogo}
        alt="Alume"
        width={120}
        style={{ margin: '12px auto 0 auto' }}
      />

      <List sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {navigationRoutes.map(({ name, route, icon, hide, isNew }) => (
          <ListItem
            key={route}
            disablePadding
            sx={{ mb: 1, display: hide ? 'none' : 'flex' }}
          >
            <ListItemButton
              component={Link}
              to={route}
              selected={pathname === route}
              sx={{
                borderRadius: '100px',
                '&.Mui-selected, &.MuiListItemButton-root:hover': {
                  bgcolor: (theme) => theme.palette.primary.light,
                },
              }}
            >
              <ListItemIcon sx={{ color: 'white' }}>{icon}</ListItemIcon>
              <ListItemText primary={name} />
              {isNew && <NewFeatureChip />}
            </ListItemButton>
          </ListItem>
        ))}

        <Box flex={1} />

        <Box sx={{ mb: 2 }}>
          <CurrentPlanCard />
        </Box>

        <ListItem disablePadding>
          <ListItemButton
            onClick={() => logout()}
            sx={{
              borderRadius: '100px',
              '&.Mui-selected, &.MuiListItemButton-root:hover': {
                bgcolor: (theme) => theme.palette.primary.light,
              },
            }}
          >
            <ListItemIcon sx={{ color: 'white' }}>
              <Logout />
            </ListItemIcon>
            <ListItemText primary="Sair da conta" />
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  )
}
